import {
  EMPTY_ARRAY,
  getAgGridColId,
  getOrderStatusColor,
  isOrderRejected,
  Order,
  prettyPriceProtection,
  PricingModeEnum,
  SecurityTooltipEnum,
  SecurityToursEnum,
  useDefaultColumns,
  type AgGridMeterProps,
  type AgGridMeterTooltip,
  type Column,
  type ColumnDef,
  type FilledPercentColumnParams,
  type Leaves,
  type MeterWithValueProps,
  type PriceParams,
  type SizeColumnParams,
  type SubAccountColumnParams,
  type TextColumnParams,
} from '@talos/kyoko';
import type { CellClassParams, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { DDHProgress } from '../../../components/DDH/DDHProgressTooltip';
import { MarketDistributionTooltipRenderer } from '../../../components/MarketDistribution/MarketDistributionTooltip';
import { OrderAnalyticsTooltipCellRenderer } from '../../../components/OrderAnalytics/OrderAnalyticsTooltip';
import { useTradingSettings } from '../../../providers/TradingSettingsContext';

export const getMeterTooltip: AgGridMeterProps['tooltip'] = (
  props: { meterProps: MeterWithValueProps } & AgGridMeterProps
): AgGridMeterTooltip | null => {
  const data: unknown = props.data;
  if (!(data instanceof Order)) {
    return null;
  }

  if (data.isDDHParentOrder) {
    return {
      content: <DDHProgress order={data} meterProps={props.meterProps} />,
    };
  }

  if (!isOrderRejected(data.OrdStatus)) {
    return {
      type: 'customPopover',
      content: <MarketDistributionTooltipRenderer {...props} />,
    };
  }

  return null;
};

const getMeterClassName = (data: Order) => {
  if (data.isDDHParentOrder) {
    return 'ddh-order-meter';
  }
  return '';
};

const DEFAULT_ORDER_COLUMNS: Map<string, ColumnDef<Order>> = new Map(
  (
    [
      { field: 'warning', type: 'warning', params: { tooltipWidth: '400px' } },
      {
        field: 'SubmitTime',
        type: 'date',
        sortable: true,
        initialSort: '-',
        title: 'Submit Date',
        params: { milliseconds: true },
        initialWidth: 140,
      },
      {
        field: 'Symbol',
        type: 'security',
        sortable: true,
        initialWidth: 100,
        params: {
          tooltip: SecurityTooltipEnum.UNIFIED_LIQUIDITY,
          tours: [SecurityToursEnum.DDH_ENABLED_ORDER],
        },
      },
      { field: 'Side', type: 'side' },
      {
        field: 'OrderQty',
        type: 'size',
        sortable: true,
        initialWidth: 120,
        params: { currencyField: 'Currency' } satisfies SizeColumnParams,
      },
      {
        field: 'Price',
        type: 'price',
        sortable: true,
        params: {
          assetField: 'Symbol',
          showReferencePrice: true,
          showPriceAsRange: true,
          /* The "Price" field is only defined in BPS space for the BPS pricing mode specifically */
          isReferencePrice: (data: Order | undefined) => data?.PricingMode === PricingModeEnum.BPS,
        } satisfies PriceParams<Order>,
      },
      {
        field: 'PricingReference',
        type: 'price',
        initialHide: true,
        params: {
          isReferencePrice: true,
        },
      },
      { field: 'Strategy', type: 'strategy', sortable: true },
      {
        field: 'CumQty',
        type: 'size',
        sortable: true,
        title: 'Filled Qty',
        params: { currencyField: 'Currency' },
        initialWidth: 120,
      },
      {
        id: 'filledPercent' satisfies ExtraColIDs,
        type: 'filledPercent',
        title: 'Filled %',
        sortable: true,
        params: {
          filledQtyField: 'CumQty',
          totalQtyField: 'OrderQty',
          getColor: (node, theme) => getOrderStatusColor(node.data, false, theme),
          tooltip: getMeterTooltip,
        } satisfies FilledPercentColumnParams<Order>,
        // For DDH tours (2.49), we apply a class to the filled % cell. This cell's class is the anchor of the tour.
        cellClass: (params: CellClassParams<Order>) => (params.node.data ? getMeterClassName(params.node.data) : ''),
      },
      {
        id: 'orderAnalytics',
        type: 'custom',
        title: 'Analytics',
        params: {
          headerName: '',
          cellRenderer: OrderAnalyticsTooltipCellRenderer,
        },
        sortable: false,
        initialWidth: 80,
      },
      { field: 'filledPx', type: 'price', sortable: true, title: 'Filled Price', params: { assetField: 'Symbol' } },
      {
        field: 'CumFee',
        type: 'size',
        sortable: true,
        title: 'Fees',
        params: { currencyField: 'FeeCurrency' },
        initialWidth: 80,
      },
      { field: 'OrdStatus', type: 'orderStatus', sortable: true, title: 'Order Status' },
      {
        field: 'HedgeOrderStatus.HedgeControlStatus',
        type: 'autohedgeStatus',
        title: 'Hedge Order Status',
        initialHide: true,
        params: {
          textField: 'HedgeOrderStatus.Text',
        },
      },
      { field: 'selectedMarkets', title: 'Selected Markets', type: 'markets', sortable: true },
      { field: 'tradedMarkets', type: 'markets', title: 'Traded Markets', sortable: true },
      {
        id: 'SubAccount',
        field: 'subAccounts',
        type: 'subAccount',
        sortable: true,
        title: 'Sub Account(s)',
        params: {
          allocationCurrencyField: 'Currency',
          allocationField: 'Allocation',
        } satisfies SubAccountColumnParams<Order>,
      },
      { field: 'User', type: 'user', sortable: true, initialWidth: 140 },
      { field: 'OrderID', type: 'id' },
      { field: 'selectedAndTradedMarketAccounts', type: 'marketAccounts', title: 'Market Accounts', initialHide: true },
      {
        field: 'AvgPx',
        type: 'price',
        sortable: true,
        initialHide: true,
        title: 'Filled Price (no fees)',
        params: { assetField: 'Symbol', showFeeIcon: 'never' },
      },
      { field: 'RiskStatus', type: 'bitmap', initialHide: true },
      {
        field: 'AvgPxAllIn',
        type: 'price',
        sortable: true,
        initialHide: true,
        title: 'Filled Price (with fees)',
        params: { assetField: 'Symbol' },
      },
      { field: 'CumAmt', type: 'size', sortable: true, initialHide: true, params: { currencyField: 'AmountCurrency' } },
      { field: 'selectedAndTradedMarkets', type: 'markets', title: 'Markets', initialHide: true },
      { field: 'selectedMarketAccounts', type: 'marketAccounts', sortable: false, initialHide: true },
      { field: 'tradedMarketAccounts', type: 'marketAccounts', sortable: false, initialHide: true },
      {
        id: 'filledNotional',
        type: 'filledNotional',
        sortable: true,
        initialHide: true,
        title: 'Filled Notional',
        params: { securityField: 'Symbol', showFeeIcon: 'never' },
      },
      {
        id: 'openNotional',
        type: 'openNotional',
        sortable: true,
        initialHide: true,
        title: 'Open Notional',
        params: { securityField: 'Symbol', showFeeIcon: 'never' },
      },
      {
        id: 'totalNotional',
        type: 'totalNotional',
        sortable: true,
        initialHide: true,
        title: 'Total Notional',
        params: { securityField: 'Symbol', showFeeIcon: 'never' },
      },
      {
        id: 'filledCounterAmount',
        type: 'filledCounterAmount',
        sortable: true,
        initialHide: true,
        title: 'Filled Counter Amt',
        params: { currencyField: 'AmountCurrency', showFeeIcon: 'never' },
      },

      { field: 'Group', type: 'text', sortable: true, initialHide: true },

      { id: 'productType', type: 'productType', sortable: true, initialHide: true, title: 'Product' },

      { field: 'endTime', type: 'date', sortable: true, initialHide: true },
      { field: 'LastTradeTime', type: 'date', sortable: true, initialHide: true },
      { field: 'startTime', type: 'date', sortable: true, initialHide: true },
      {
        field: 'Timestamp',
        type: 'date',
        sortable: true,
        initialHide: true,
        title: 'Updated at',
        params: { milliseconds: true },
      },
      { id: 'baseQty', type: 'cumBaseQty', sortable: true, initialHide: true, title: 'Filled Base Qty' },
      { field: 'OrdType', type: 'text', sortable: true, initialHide: true, title: 'Order Type' },

      { field: 'ClOrdID', type: 'id', initialHide: true },
      {
        field: 'LeavesQty',
        type: 'size',
        sortable: true,
        initialHide: true,
        params: { currencyField: 'Currency' },
        title: 'Open Qty',
      },
      { field: 'remainQty', type: 'size', sortable: true, initialHide: true, params: { currencyField: 'Currency' } },
      { field: 'TimeInForce', type: 'text', sortable: true, initialHide: true },
      { field: 'Comments', type: 'text', sortable: true, initialHide: true },
      { field: 'DecisionStatus', type: 'text', sortable: true, initialHide: true },
      { field: 'ExecID', type: 'text', sortable: true, initialHide: true },
      { field: 'TransactTime', type: 'date', sortable: true, initialHide: true },
      { field: 'Text', type: 'text', sortable: true, initialHide: true },
      { field: 'Parameters', type: 'orderParameters', initialHide: true },
      {
        field: 'ExpectedFillPrice',
        type: 'price',
        sortable: true,
        initialHide: true,
        params: { assetField: 'Symbol', showFeeIcon: 'never' },
      },
      {
        field: 'ExpectedFillQty',
        type: 'size',
        sortable: true,
        initialHide: true,
        params: { currencyField: 'Currency' },
      },
      { field: 'ParentOrderID', type: 'text', sortable: true, initialHide: true },
      { field: 'Revision', type: 'text', sortable: true, initialHide: true },
      { field: 'OrigClOrdID', type: 'text', sortable: true, initialHide: true },
      { field: 'SessionID', type: 'text', sortable: true, initialHide: true },
      { field: 'Allocation', type: 'text', sortable: true, initialHide: true },
      { field: 'CxlRejReason', type: 'text', sortable: true, initialHide: true },
      { field: 'AggressorSide', type: 'side', sortable: true, initialHide: true },
      {
        field: 'allowedSlippageBPS',
        title: 'Allowed Slippage',
        type: 'number',
        sortable: true,
        initialHide: true,
        params: { currency: 'BPS' },
      },
      {
        type: 'custom',
        params: { valueFormatter: ({ value }) => prettyPriceProtection(value) },
        field: 'priceProtection',
        initialHide: true,
        sortable: true,
      },
      { field: 'ParentRFQID', type: 'id', sortable: true, initialHide: true },
      { field: 'QuoteID', type: 'id', sortable: true, initialHide: true },
      { field: 'RFQID', type: 'id', sortable: true, initialHide: true },
      { field: 'unifiedLiquidity', type: 'text', initialHide: true, sortable: true },
      { field: 'reduceOnly', type: 'text', initialHide: true, sortable: true },
      { field: 'reduceFirst', type: 'text', initialHide: true, sortable: true },
      {
        field: 'fixingIndices',
        title: 'Fixing Index',
        type: 'fixingIndices',
        id: 'fixingIndices',
        initialHide: true,
      },
      {
        field: 'FixingDetails',
        title: 'Fixing Price',
        type: 'fixingPrice',
        id: 'fixingPrice',
        initialHide: true,
      },
      { field: 'PricingMode', type: 'text', title: 'Pricing Mode', initialHide: true },
      {
        field: 'isArchived',
        type: 'text',
        title: 'Archived',
        initialHide: true,
        params: { getLabel: val => (val ? 'Y' : 'N') } satisfies TextColumnParams,
      },
      {
        field: 'Parameters.TriggerPrice',
        title: 'Trigger Price',
        type: 'price',
        initialHide: true,
        params: { assetField: 'Symbol' satisfies Leaves<Order>, showReferencePrice: true } satisfies PriceParams,
      },
      {
        field: 'RequestSource',
        title: 'Request Source',
        type: 'text',
        initialHide: true,
      },
    ] satisfies ColumnDef<Order>[]
  ).map(c => [getAgGridColId(c), c])
);

const CUSTOMER_ORDER_COLUMNS: Map<string, ColumnDef<Order>> = new Map(
  (
    [
      {
        field: 'customerOrder', // a.k.a Customer Order Counterparty
        title: 'Cust Order',
        type: 'custom',
        sortable: true,
        headerGroupToggle: true,
        columnsInGroup: [
          'customerOrderID',
          'customerAccount',
          'customerQuantity',
          'customerFilledQuantity',
          'customerPrice',
          'customerAvgPx',
          'customerFilledCumAmt',
        ],
        columnGroup: true,
        initialWidth: 120,
        minWidth: 100,
        params: {
          valueFormatter: ({ value, context, data }: ValueFormatterParams<Order>) => {
            const customerOrderCounterparty = context.current?.customersByName?.get(value)?.DisplayName || value;
            if (!customerOrderCounterparty && data?.ParentOrderID && data.RequestSource === 'Customer') {
              // Show that there is an associated customer order (ParentOrderID) even if we haven't explicitly loaded it yet
              // Should be rare, but we can look at rendering this as an icon with a tooltip instead if it ends up being common
              return 'Customer order';
            }
            return customerOrderCounterparty;
          },
        },
      },
      {
        field: 'customerOrderID',
        title: 'Cust Order ID',
        type: 'id',
        columnGroup: true,
        initialWidth: 100,
        minWidth: 80,
      },
      {
        field: 'customerAccount',
        title: 'Cust Account',
        type: 'marketAccountSourceAccountID',
        sortable: true,
        columnGroup: true,
        initialWidth: 100,
        minWidth: 80,
      },
      {
        field: 'customerQuantity',
        title: 'Cust Qty',
        type: 'size',
        sortable: true,
        params: { currencyField: 'Currency' },
        initialWidth: 100,
        minWidth: 80,
        columnGroup: true,
      },
      {
        field: 'customerFilledQuantity',
        title: 'Cust Filled Qty',
        type: 'size',
        sortable: true,
        params: { currencyField: 'Currency' },
        initialWidth: 120,
        minWidth: 80,
        columnGroup: true,
      },
      {
        field: 'customerPrice',
        title: 'Cust Limit Price',
        type: 'price',
        sortable: true,
        params: { assetField: 'Symbol' },
        initialWidth: 120,
        minWidth: 80,
        columnGroup: true,
      },
      {
        field: 'customerAvgPx',
        title: 'Cust Avg. Filled Price',
        type: 'price',
        sortable: true,
        params: { assetField: 'Symbol' },
        initialWidth: 160,
        minWidth: 80,
        columnGroup: true,
      },
      {
        field: 'customerFilledCumAmt',
        title: 'Cust Filled Counter Amt',
        type: 'price',
        sortable: true,
        params: { quoteCurrencyField: 'customerAmountCurrency' },
        initialWidth: 160,
        minWidth: 80,
        columnGroup: true,
      },
    ] satisfies ColumnDef<Order>[]
  ).map(c => [getAgGridColId(c), c])
);

// ColIDs which aren't a field on the Order entity (this shouldn't happen, means column needs to be refactored)
type ExtraColIDs = 'filledPercent';

export const useOrderColumns = ({
  defaultColumns = EMPTY_ARRAY,
  suppressCustomerColumns,
}: {
  defaultColumns?: (keyof Order | ExtraColIDs | Partial<Column<Order>>)[];
  suppressCustomerColumns?: boolean; // Skip showing Customer columns
}) => {
  const { enableCustomerBlotterColumns } = useTradingSettings();

  const columnDefinitions = useMemo(() => {
    const definitionsMap = new Map<string, ColumnDef<Order>>(DEFAULT_ORDER_COLUMNS.entries());
    if (enableCustomerBlotterColumns && !suppressCustomerColumns) {
      definitionsMap.clear();
      const cols = [...DEFAULT_ORDER_COLUMNS.values()]
        .slice(0, 2)
        .concat([...CUSTOMER_ORDER_COLUMNS.values()])
        .concat([...DEFAULT_ORDER_COLUMNS.values()].slice(2));
      cols.forEach(col => {
        definitionsMap.set(getAgGridColId(col), col);
      });
    }

    return definitionsMap as Map<string, Column>;
  }, [enableCustomerBlotterColumns, suppressCustomerColumns]);

  return useDefaultColumns(defaultColumns, columnDefinitions);
};
